import {
  Add,
  DesktopMacOutlined,
  DeveloperBoard,
  LaptopMac,
  PhoneIphone,
  Print,
  Router,
  Storage,
  Tablet,
  Tv,
} from "@mui/icons-material";
import { SvgIconComponent } from "@mui/icons-material";
import { PickupEstimatedItem } from "types/pickupEstimatedItem";

export interface PickupItemDefinition {
  name: string;
  label: string;
  isTextField?: boolean;
}

export interface PickupItemCategory {
  key: string;
  title: string;
  icon: SvgIconComponent;
  items: PickupItemDefinition[];
  badge?: "optional";
}

export interface MonitorDetails {
  size: string;
  attachedToStands: string;
}

export const MONITOR_SIZE_OPTIONS = ["Less than 32 inches", "32 inches or larger"] as const;
export const MONITOR_STAND_OPTIONS = ["Yes", "No"] as const;

export const DEFAULT_MONITOR_DETAILS: MonitorDetails = {
  size: MONITOR_SIZE_OPTIONS[0],
  attachedToStands: MONITOR_STAND_OPTIONS[0],
};

export const PICKUP_ITEM_CATEGORIES: PickupItemCategory[] = [
  {
    key: "laptops",
    title: "Laptops",
    icon: LaptopMac,
    items: [
      { name: "Apple Laptops", label: "Apple Laptops" },
      { name: "PC Laptops", label: "PC Laptops" },
      { name: "Chromebooks", label: "Chromebooks" },
    ],
  },
  {
    key: "desktops",
    title: "Desktops",
    icon: DesktopMacOutlined,
    items: [
      { name: "Desktops", label: "Desktops" },
      { name: "Mac Minis", label: "Mac Minis" },
      { name: "AIO", label: "AIO (All-In-One)" },
    ],
  },
  {
    key: "tablets",
    title: "Tablets",
    icon: Tablet,
    items: [
      { name: "iPads", label: "iPads" },
      { name: "Tablets", label: "Tablets (Non-Apple)" },
    ],
  },
  {
    key: "mobile-phones",
    title: "Mobile Phones",
    icon: PhoneIphone,
    items: [
      { name: "iPhones", label: "iPhones" },
      { name: "Mobile Phones", label: "Mobile Phones (Non-Apple)" },
    ],
  },
  {
    key: "displays",
    title: "Displays",
    icon: Tv,
    items: [
      { name: "Monitors", label: "Monitors" },
      { name: "Televisions", label: "Televisions" },
    ],
  },
  {
    key: "office-equipment",
    title: "Office Equipment",
    icon: Print,
    items: [
      { name: "Office Phones", label: "Office Phones" },
      { name: "Printers", label: "Printers" },
      { name: "Copiers", label: "Copiers" },
    ],
  },
  {
    key: "networking-equipment",
    title: "Networking Equipment",
    icon: Router,
    items: [
      { name: "Servers", label: "Servers" },
      { name: "Server Rack", label: "Server Rack" },
      { name: "Networking Equipment", label: "Networking Equipment (switches, routers, APs)" },
    ],
  },
  {
    key: "other",
    title: "Other",
    icon: DeveloperBoard,
    items: [
      { name: "Large Box", label: "Large Box of Misc" },
      { name: "Small Box", label: "Small Box of Misc" },
      { name: "Commercial Bin", label: "Commercial Bin" },
      { name: "Other", label: "Other", isTextField: true },
    ],
  },
  {
    key: "storage",
    title: "Storage",
    icon: Storage,
    items: [{ name: "HDD", label: "HDD (Hard Disk Drives)" }],
  },
  {
    key: "additional-items",
    title: "Additional Items",
    icon: Add,
    items: [],
    badge: "optional",
  },
];

const ALL_ITEM_NAMES = PICKUP_ITEM_CATEGORIES.flatMap((category) =>
  category.items.filter((item) => !item.isTextField).map((item) => item.name)
);

const LEGACY_ITEM_NAMES: Record<string, string> = {
  Laptops: "Apple Laptops",
};

export type ItemQuantities = Record<string, string>;

export function createEmptyQuantities(): ItemQuantities {
  return ALL_ITEM_NAMES.reduce<ItemQuantities>((acc, name) => {
    acc[name] = "";
    return acc;
  }, {});
}

export function parseQuantity(value: string): number {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : 0;
}

export function hasQuantityValue(value: string | undefined): boolean {
  return value !== undefined && value !== "" && parseQuantity(value) > 0;
}

function formatMonitorDescription(details: MonitorDetails) {
  return `Size: ${details.size}; Attached to stands or arms: ${details.attachedToStands}`;
}

function parseMonitorDescription(description: string): MonitorDetails {
  const sizeMatch = description.match(/Size:\s*([^;]+)/i);
  const attachedMatch = description.match(/Attached to stands or arms:\s*(.+)/i);

  return {
    size: sizeMatch?.[1]?.trim() || DEFAULT_MONITOR_DETAILS.size,
    attachedToStands: attachedMatch?.[1]?.trim() || DEFAULT_MONITOR_DETAILS.attachedToStands,
  };
}

export function parseEstimatedItems(items: PickupEstimatedItem[]) {
  const quantities = createEmptyQuantities();
  let otherDescription = "";
  let additionalItems = "";
  let monitorDetails = DEFAULT_MONITOR_DETAILS;

  items.forEach((item) => {
    if (item.name === "Other") {
      otherDescription = item.description || "";
      return;
    }

    if (item.name === "Additional Items") {
      additionalItems = item.description || "";
      return;
    }

    const itemName = LEGACY_ITEM_NAMES[item.name] || item.name;

    if (itemName === "Monitors") {
      quantities.Monitors = String(item.quantity);
      if (item.description) {
        monitorDetails = parseMonitorDescription(item.description);
      }
      return;
    }

    if (itemName in quantities) {
      quantities[itemName] = String(item.quantity);
    }
  });

  return { quantities, otherDescription, additionalItems, monitorDetails };
}

export function buildEstimatedItems(
  quantities: ItemQuantities,
  otherDescription: string,
  additionalItems: string,
  monitorDetails: MonitorDetails
): PickupEstimatedItem[] {
  const items: PickupEstimatedItem[] = [];

  Object.entries(quantities).forEach(([name, value]) => {
    const quantity = parseQuantity(value);
    if (quantity <= 0) {
      return;
    }

    const item: PickupEstimatedItem = { name, quantity };

    if (name === "Monitors") {
      item.description = formatMonitorDescription(monitorDetails);
    }

    items.push(item);
  });

  if (otherDescription.trim()) {
    items.push({ name: "Other", quantity: 1, description: otherDescription.trim() });
  }

  if (additionalItems.trim()) {
    items.push({ name: "Additional Items", quantity: 1, description: additionalItems.trim() });
  }

  return items;
}

export function getCategoryItemCount(category: PickupItemCategory, quantities: ItemQuantities) {
  return category.items.reduce((total, item) => {
    if (item.isTextField) {
      return total;
    }

    return total + parseQuantity(quantities[item.name] || "");
  }, 0);
}

export function hasEstimatedItemData(quantities: ItemQuantities, otherDescription: string, additionalItems: string) {
  const hasQuantity = Object.values(quantities).some(hasQuantityValue);
  return hasQuantity || otherDescription.trim().length > 0 || additionalItems.trim().length > 0;
}
